import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Save, SkipForward, Edit3, Heart } from 'lucide-react'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'

interface NoteInputProps {
  selectedMood: number
  onSave: (note: string) => void
  onSkip: () => void
}

const moodData = {
  1: { label: 'Molto Male', emoji: '😢', color: 'from-red-400 to-red-600', bgColor: 'from-red-50 to-red-100' },
  2: { label: 'Male', emoji: '😕', color: 'from-orange-400 to-orange-600', bgColor: 'from-orange-50 to-orange-100' },
  3: { label: 'Neutro', emoji: '😐', color: 'from-yellow-400 to-yellow-600', bgColor: 'from-yellow-50 to-yellow-100' },
  4: { label: 'Bene', emoji: '😊', color: 'from-purple-400 to-purple-600', bgColor: 'from-purple-50 to-purple-100' },
  5: { label: 'Molto Bene', emoji: '🥰', color: 'from-purple-400 to-purple-600', bgColor: 'from-purple-50 to-purple-100' }
}

export default function NoteInput({ selectedMood, onSave, onSkip }: NoteInputProps) {
  const [note, setNote] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  const mood = moodData[selectedMood as keyof typeof moodData]

  const handleSave = () => {
    onSave(note)
  }

  return (
    <div className="space-y-8">
      <motion.div 
        className="text-center"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br ${mood.bgColor} rounded-full mb-4 shadow-xl border-4 border-white`}
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="text-4xl">{mood.emoji}</div>
        </motion.div>
        
        <motion.h2 
          className={`text-2xl font-bold bg-gradient-to-r ${mood.color} bg-clip-text text-transparent mb-2`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {mood.label}
        </motion.h2>
        
        <motion.p 
          className="text-gray-600 text-lg"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Vuoi condividere qualcosa? ✍️
        </motion.p>
      </motion.div>

      <motion.div 
        className="space-y-6"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="relative">
          <motion.div
            className="absolute top-3 left-3 z-10"
            animate={{ 
              scale: isTyping ? [1, 1.2, 1] : 1,
              rotate: isTyping ? [0, 10, -10, 0] : 0
            }}
            transition={{ duration: 0.5 }}
          >
            <Edit3 className="w-5 h-5 text-gray-400" />
          </motion.div>
          
          <Textarea
            placeholder="Racconta cosa ha reso speciale questo momento, cosa ti ha fatto sentire così, o semplicemente i tuoi pensieri..."
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setIsTyping(e.target.value.length > 0)
            }}
            className="min-h-[140px] resize-none pl-12 text-base leading-relaxed border-2 border-gray-200 focus:border-purple-300 rounded-2xl shadow-lg bg-white/80 backdrop-blur-sm"
            maxLength={300}
          />
        </div>
        
        <div className="flex justify-between items-center">
          <motion.div 
            className="text-sm text-gray-500"
            animate={{ opacity: note.length > 250 ? 1 : 0.6 }}
          >
            {note.length}/300 caratteri
          </motion.div>
          
          {note.length > 0 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex items-center space-x-1 text-purple-600"
            >
              <Heart className="w-4 h-4" />
              <span className="text-sm font-medium">Grazie per aver condiviso!</span>
            </motion.div>
          )}
        </div>
      </motion.div>

      <motion.div 
        className="space-y-4"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button 
            onClick={handleSave}
            className={`w-full bg-gradient-to-r ${mood.color} hover:shadow-lg text-white border-0 h-14 text-lg font-semibold rounded-2xl shadow-xl`}
            size="lg"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Save className="w-6 h-6 mr-3" />
            </motion.div>
            Salva il Momento
          </Button>
        </motion.div>
        
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button 
            onClick={onSkip}
            variant="outline"
            className="w-full h-12 text-base font-medium rounded-2xl border-2 border-gray-200 hover:border-gray-300 bg-white/80 backdrop-blur-sm"
            size="lg"
          >
            <SkipForward className="w-5 h-5 mr-2" />
            Salta per Ora
          </Button>
        </motion.div>
      </motion.div>

      <motion.div
        className="text-center pt-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <p className="text-gray-500 text-sm">
          Le tue parole rendono ogni emozione più significativa 💭
        </p>
      </motion.div>
    </div>
  )
}